<template>
  <div>
    <h2>Payment Details</h2>

    <!-- Loading and Error Messages -->
    <div v-if="loading" class="status-message loading">
      Loading card elements...
    </div>
    <div v-if="error" class="status-message error">{{ error }}</div>

    <!-- Card Form -->
    <div id="card-form" v-show="paymentInitiated">
      <p v-if="loadingElements" class="status-message">
        Loading card elements...
      </p>

      <div id="card-container" v-show="!loadingElements">
        <div class="card-element">
          <!-- Split card input containers -->
          <div class="input-container">
            <div id="cardNumber" class="input-field"></div>
          </div>
          <div class="input-row">
            <div class="input-container half">
              <div id="expiry" class="input-field"></div>
            </div>
            <div class="input-container half">
              <div id="cvc" class="input-field"></div>
            </div>
          </div>
        </div>

        <button
          type="button"
          id="confirm-button"
          @click="confirmPayment"
          :disabled="!allFieldsComplete || processingPayment"
          class="submit-button"
        >
          {{ processingPayment ? "Processing..." : "Pay $" + displayAmount }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createElement,
  confirmPaymentIntent,
  loadAirwallex,
  getElement,
} from "airwallex-payment-elements";
import api from "../../utils/api";

export default {
  props: {
    price: {
      type: [Number, String],
      required: true,
      default: 0,
    },
    firstName: {
      type: String,
      required: true,
      default: "",
    },
    email: {
      type: String,
      required: true,
      default: "",
    },
    bookingType: {
      type: String,
      required: false,
      default: "",
    },
  },
  name: "SplitCard",
  data() {
    return {
      // Form state
      amount: 0,
      paymentInitiated: false,
      loading: false,
      loadingElements: false,
      error: null,
      processingPayment: false,
      paymentSuccess: false,
      transactionId: null,

      // Intent details
      intent_id: null,
      client_secret: null,

      // Element state tracking
      elementsReady: { cardNumber: false, expiry: false, cvc: false },
      elementsComplete: { cardNumber: false, expiry: false, cvc: false },
    };
  },
  computed: {
    allFieldsComplete() {
      return Object.values(this.elementsComplete).every(Boolean);
    },
    displayAmount() {
      return parseFloat(this.amount).toFixed(2);
    },
  },
  mounted() {
    // Convert price to a number and handle any potential issues
    this.amount =
      typeof this.price === "string" ? parseFloat(this.price) : this.price;

    // Ensure we have a valid amount
    if (isNaN(this.amount) || this.amount <= 0) {
      this.error = "Invalid price amount";
      console.error("Invalid price:", this.price);
      return;
    }

    // Initialize payment
    this.initiatePayment();
  },
  methods: {
    async initiatePayment() {
      this.loading = true;
      this.error = null;

      try {
        const response = await api.post("/property/create-payment-intent", {
          amount: this.amount,
          first_name: this.firstName,
          email: this.email,
          booking_type: this.bookingType,
          third_party_type: this.bookingType,
        });

        // Save the returned data
        this.intent_id = response.data.data.payment_intent_id;
        this.client_secret = response.data.data.client_secret;

        this.paymentInitiated = true;
        this.loadingElements = true;

        this.initializeCardElements();
      } catch (err) {
        console.error("Payment initiation error:", err);
        this.error =
          err.response?.data?.error ||
          "Failed to create payment. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    initializeCardElements() {
      const apiUrl = window.APP_URL || '';
      const isProd = apiUrl.includes('bookingaggregator.com') && !apiUrl.includes('staging') && !apiUrl.includes('demo');
      const airwallexEnv = isProd ? 'prod' : 'demo';

      loadAirwallex({
        env: airwallexEnv, // Use 'prod' for production and 'demo' for sandbox
        origin: window.location.origin,
        fonts: [
          {
            src: "https://checkout.airwallex.com/fonts/CircularXXWeb/CircularXXWeb-Regular.woff2",
            family: "AxLLCircular",
            weight: 400,
          },
        ],
      })
        .then(() => {
          const cardNumEle = createElement("cardNumber",{
            autoCapture: this.bookingType === 'R2B' ? false : true,
          });
          const cvcEle = createElement("cvc");
          const expiryEle = createElement("expiry");

          cardNumEle.mount("cardNumber");
          cvcEle.mount("cvc");
          expiryEle.mount("expiry");

          this.setupEventListeners();
        })
        .catch((err) => {
          this.error = "Failed to load payment form. Please try again.";
          console.error("Error loading Airwallex:", err);
          this.loadingElements = false;
        });
    },

    setupEventListeners() {
      window.addEventListener("onReady", this.handleReady);
      window.addEventListener("onChange", this.handleChange);
      window.addEventListener("onError", this.handleError);
    },

    handleReady(event) {
      const { type } = event.detail;
      if (Object.prototype.hasOwnProperty.call(this.elementsReady, type)) {
        this.elementsReady[type] = true;
      }

      if (!Object.values(this.elementsReady).includes(false)) {
        this.loadingElements = false;
      }
    },

    handleChange(event) {
      const { type, complete } = event.detail;
      if (Object.prototype.hasOwnProperty.call(this.elementsComplete, type)) {
        this.elementsComplete[type] = complete;
      }
    },

    handleError(event) {
      const { error } = event.detail;
      this.error = error.message;
      console.error("Element error:", error);
    },

    async confirmPayment() {
      if (!this.allFieldsComplete) {
        console.log("Not all fields are complete");
        return;
      }

      this.processingPayment = true;
      this.error = null;

      try {
        const cardNumElement = getElement("cardNumber");

        if (!cardNumElement) {
          this.error = "Card number element is not available";
          console.error("Card number element not found");
          return;
        }

        const payload = {
          element: cardNumElement,
          id: this.intent_id,
          client_secret: this.client_secret,
        };

        const response = await confirmPaymentIntent({
          element: cardNumElement,
          id: this.intent_id,
          client_secret: this.client_secret,
          payment_method: {
            billing: {
              name: this.firstName
            }
          },
        });

        if (response?.id) {
          this.paymentSuccess = true;
          this.transactionId = response.id || this.intent_id;

          this.$emit("payment-success", response);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        console.error("Payment confirmation error:", err);
        this.error =
          err.response?.data?.error || "Payment failed. Please try again.";
        this.$emit("payment-error", {
          error: this.error,
        });
      } finally {
        this.processingPayment = false;
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener("onReady", this.handleReady);
    window.removeEventListener("onChange", this.handleChange);
    window.removeEventListener("onError", this.handleError);
  },
};
</script>

<style scoped>
.status-message {
  padding: 10px;
  margin: 10px 0;
  border-radius: 4px;
}

.loading {
  background-color: #f8f9fa;
  color: #6c757d;
}

.error {
  background-color: #f8d7da;
  color: #721c24;
}

.success {
  background-color: #d4edda;
  color: #155724;
}

.card-element {
  margin-bottom: 24px;
}

.input-container {
  margin-bottom: 2px;
}

.input-row {
  display: flex;
  gap: 10px;
}

.half {
  flex: 1;
}

.input-field {
  border: 1px solid #e1dede;
  border-radius: 4px;
  padding: 10px;
}

.input-field:focus-within {
  border-color: #5ab79c;
  box-shadow: 0 1px 3px rgba(97, 47, 255, 0.1), 0 1px 2px rgba(97, 47, 255, 0.2);
}

.submit-button {
  display: block;
  width: 100%;
  height: 44px;
  padding: 12px;
  background-color: #5ab79c;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: background-color 150ms ease;
  margin-bottom: 10px;
}

.submit-button:hover {
  background-color: #5AB79CCC;
}

.submit-button:active {
  background-color: #5ab79c;
}

.submit-button:disabled {
  background-color: #5ab79c;
  cursor: not-allowed;
}

.btn-secondary {
  display: block;
  width: 100%;
  height: 44px;
  padding: 12px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5c636a;
}

.btn-secondary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>